/**
 * companyCreate.js — the rules behind the unified Create control on
 * /companies (SOUND-296).
 *
 * PURE. No React, no network. This exists because sound-app has no
 * component-test harness: lint and a production build cannot catch a field
 * that renders for the wrong type, a menu that offers a partner user an
 * option they must never have, or a payload that quietly stops seeding a
 * client's registration. Those rules live here so they can be asserted.
 *
 * ── The three creatable things ──────────────────────────────────────────
 *
 * "Parent Company" is NOT a company type. `companyType` is
 * `client | partner | processor | internal`; a parent company is a
 * `companyType: "client"` with no `parentCompanyId` that other clients point
 * at. The only real difference between creating a client and creating a
 * parent is the skeleton application — a holding company does not process,
 * so it has no registration (verified: 11 parent companies in prod, 1 with an
 * application, and that one is a test record).
 *
 * So the menu offers two company types and one hierarchy role. That is fine
 * as a USER-FACING menu — it is how the team talks — but nothing below may
 * invent a third `companyType`.
 *
 * ── The hard constraint ─────────────────────────────────────────────────
 *
 * Partners must NEVER create a Parent Company or a Partner (CPO, explicit).
 * `createTypeOptionsFor()` is the enforcement, and it keys off `isInternal()`
 * and nothing else. This is currently the ONLY enforcement that exists: a
 * "parent" create is a client create with no application, so the API cannot
 * tell the two apart. Do not make this conditional on a role, a feature flag
 * or a company id.
 */

import { isInternal } from "./userType";
import { CLIENT_TIER_OPTIONS, PARTNER_TIER_OPTIONS } from "../constants/companies";

/** The three options the Create control can offer. */
export const CREATE_TYPES = Object.freeze({
  CLIENT: "client",
  PARENT: "parent",
  PARTNER: "partner",
});

/**
 * Presentation + authorization metadata per creatable type.
 *
 * `internalOnly` is the machine-readable half of the hard constraint above.
 */
export const CREATE_TYPE_META = Object.freeze({
  client: Object.freeze({
    type: "client",
    label: "Client",
    menuDescription: "A merchant that will process. Seeds a registration.",
    modalTitle: "Create Client",
    modalSubtitle:
      "Draft a new client. You can fill in the full registration on the next screen.",
    submitLabel: "Create Client",
    successTitle: "Client created",
    namePlaceholder: "e.g. Acme Widgets",
    internalOnly: false,
  }),
  parent: Object.freeze({
    type: "parent",
    label: "Parent Company",
    menuDescription: "A holding company other clients link under. No registration.",
    modalTitle: "Create Parent Company",
    modalSubtitle: "A new client company you can link other clients to.",
    submitLabel: "Create Parent Company",
    successTitle: "Parent company created",
    namePlaceholder: "e.g. Stallone Holdings",
    internalOnly: true,
  }),
  partner: Object.freeze({
    type: "partner",
    label: "Partner",
    menuDescription: "A referral, sourcing or ISV partner. No registration.",
    modalTitle: "Create Partner",
    modalSubtitle: "A partner company that refers clients to Sound.",
    submitLabel: "Create Partner",
    successTitle: "Partner created",
    namePlaceholder: "e.g. Media Resellers",
    internalOnly: true,
  }),
});

/** Menu order. Client is first because it is the overwhelmingly common action. */
export const CREATE_TYPE_ORDER = Object.freeze(["client", "parent", "partner"]);

/**
 * Which create options a user may see.
 *
 * internal → Client · Parent Company · Partner
 * everyone else → Client ONLY
 *
 * `/companies` is route-guarded to internal + partner, so "everyone else"
 * here means partner. The rule is written as a default-deny anyway so a new
 * user type can never fall through into partner creation.
 *
 * @param {object} user - auth user
 * @returns {Array<object>} CREATE_TYPE_META entries, in menu order
 */
export function createTypeOptionsFor(user) {
  const internal = isInternal(user);
  return CREATE_TYPE_ORDER.map((t) => CREATE_TYPE_META[t]).filter(
    (meta) => internal || !meta.internalOnly
  );
}

/**
 * Defensive re-check of the same rule, for the modal itself — a caller that
 * sets `type` from anywhere other than the menu still cannot get a partner
 * user into the partner/parent form.
 *
 * @param {object} user
 * @param {string} type
 * @returns {boolean}
 */
export function canCreateType(user, type) {
  const meta = CREATE_TYPE_META[type];
  if (!meta) return false;
  return meta.internalOnly ? isInternal(user) : true;
}

/**
 * Every field the unified modal knows how to render. The modal renders
 * exactly this vocabulary and nothing else, so `visibleFieldsFor()` below is
 * the complete answer to "what is on screen".
 */
export const CREATE_FIELD_KEYS = Object.freeze([
  "name",
  "slug",
  "legalName",
  "businessEmail",
  "tier",
  "features",
  "parentCompany",
]);

/**
 * The field matrix (plan of record, 2026-08-19).
 *
 *                    | Client              | Parent      | Partner
 *   Name             | required            | required    | required
 *   Slug             | auto — omitted      | optional    | optional
 *   Legal Name       | → application       | —           | —
 *   Business Email   | → application       | —           | —
 *   Tier             | client tiers        | client tiers| partner tiers
 *   Features         | yes                 | yes         | yes
 *   Parent picker    | internal only       | —           | —
 *
 * Client omits Slug because sound-api derives it from the name (SOUND-278);
 * offering an input that the client path never sends would be a lie.
 *
 * @param {string} type
 * @param {object} user
 * @returns {string[]} field keys, in render order
 */
export function visibleFieldsFor(type, user) {
  const internal = isInternal(user);
  switch (type) {
    case CREATE_TYPES.CLIENT:
      return [
        "name",
        "legalName",
        "businessEmail",
        "tier",
        "features",
        ...(internal ? ["parentCompany"] : []),
      ];
    case CREATE_TYPES.PARENT:
    case CREATE_TYPES.PARTNER:
      return ["name", "slug", "tier", "features"];
    default:
      return [];
  }
}

/**
 * Tier vocabulary for a create type. "Parent" is a client, so it gets client
 * tiers; only "partner" gets Sourcing / Referral / ISV.
 *
 * @param {string} type
 * @returns {string[]}
 */
export function tierOptionsForCreateType(type) {
  return type === CREATE_TYPES.PARTNER
    ? PARTNER_TIER_OPTIONS
    : CLIENT_TIER_OPTIONS;
}

/** Blank form state. One shape for all three types. */
export function emptyCreateForm() {
  return {
    name: "",
    slug: "",
    legalName: "",
    businessEmail: "",
    tier: "",
    features: [],
    parentCompany: null, // { companyId, name, slug } | null
  };
}

/**
 * Name is the only required field on every type.
 *
 * @returns {{ field: string, message: string } | null}
 */
export function validateCreateForm(form) {
  if (!String(form?.name || "").trim()) {
    return { field: "name", message: "Company name is required" };
  }
  return null;
}

/**
 * Build the exact `POST /companies` body for a type.
 *
 * Invariants, all covered by tests:
 *  - CLIENT always carries `application` — even empty. A name-only create
 *    without it produced orphan companies with no registration to fill in.
 *  - PARENT and PARTNER never carry `application`.
 *  - Only PARTNER sends `companyType`; client and parent let the API default.
 *  - Only CLIENT sends `parentCompanyId`, and only for an internal user
 *    (the API refuses it from a partner anyway — this keeps the request honest).
 *  - CLIENT never sends `slug`; the API derives it.
 *  - A tier that is not in the type's vocabulary is dropped rather than sent,
 *    so a stale selection can never put a partner on a merchant tier.
 *
 * @param {{ type: string, form: object, user: object }} args
 * @returns {object} request body
 */
export function buildCreatePayload({ type, form, user }) {
  const fields = new Set(visibleFieldsFor(type, user));
  const payload = { name: String(form.name || "").trim() };

  if (fields.has("slug")) {
    const slug = String(form.slug || "").trim();
    if (slug) payload.slug = slug;
  }

  if (fields.has("tier")) {
    const tier = String(form.tier || "").trim();
    if (tier && tierOptionsForCreateType(type).includes(tier)) {
      payload.tier = tier;
    }
  }

  if (fields.has("features")) {
    const features = Array.isArray(form.features) ? form.features : [];
    if (features.length > 0) payload.features = features;
  }

  if (type === CREATE_TYPES.CLIENT) {
    // ALWAYS sent, blank or not — the skeleton is what the Registration tab
    // then edits. Do not make this conditional on the two optional inputs.
    const businessInfo = {};
    if (fields.has("legalName")) {
      const legalName = String(form.legalName || "").trim();
      if (legalName) businessInfo.legalName = legalName;
    }
    if (fields.has("businessEmail")) {
      const businessEmail = String(form.businessEmail || "").trim();
      if (businessEmail) businessInfo.businessEmail = businessEmail;
    }
    payload.application = { businessInfo };

    if (fields.has("parentCompany") && form.parentCompany?.companyId) {
      payload.parentCompanyId = form.parentCompany.companyId;
    }
  }

  if (type === CREATE_TYPES.PARTNER) {
    payload.companyType = "partner";
  }

  return payload;
}

/**
 * Where the user lands after a successful create.
 *
 * A client has a registration waiting to be filled in, so it lands on the
 * Registration tab. A parent and a partner have no registration — sending
 * them to `?tab=application` would open an empty tab for a record that will
 * never have one.
 *
 * @param {string} type
 * @param {string} companyId
 * @returns {string | null}
 */
export function createSuccessPath(type, companyId) {
  if (!companyId) return null;
  const base = `/companies/${companyId}`;
  return type === CREATE_TYPES.CLIENT ? `${base}?tab=application` : base;
}
